import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Facebook, Zap } from "lucide-react";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import InstallPWAButton from "@/components/InstallPWAButton";
import logoMark from "@/assets/logo-mark.png";

export default function Login() {
  const { login, loginOtpRequest, loginOtpVerify } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("password"); // password | otp
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [dev, setDev] = useState("");
  const [busy, setBusy] = useState(false);

  const goHome = (u) => {
    const from = location.state?.from;
    navigate(from && from !== "/login" ? from : homeForRole(u.role), { replace: true });
  };

  const submitPassword = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const u = await login(identifier, password); toast.success("Connexion réussie"); goHome(u); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return toast.error("Renseignez votre e-mail ou téléphone");
    setBusy(true);
    try { const d = await loginOtpRequest(identifier); setSent(true); setDev(d.dev_code || ""); toast.success("Si un compte existe, un code vient d'être envoyé"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const u = await loginOtpVerify(identifier, code); toast.success("Connexion réussie"); goHome(u); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <AuthLayout title="Connexion" subtitle="Accédez à votre espace TOGOBATI">
      {mode === "password" ? (
        <>
          <button type="button" onClick={() => { setMode("otp"); setSent(false); setCode(""); setDev(""); }}
            className="sm:hidden w-full mb-4 flex items-center justify-center gap-2 border-2 border-[#E08B00] bg-[#FDF3EE] text-[#0F172A] font-bold py-3 rounded-lg active:scale-95 transition" data-testid="mobile-quick-otp">
            <Zap className="w-4 h-4 text-[#E08B00]" /> Connexion rapide par code
          </button>
          <form onSubmit={submitPassword} className="space-y-4">
            <Field label="Email ou téléphone" value={identifier} onChange={setIdentifier} testid="login-identifier" placeholder="vous@exemple.com ou +228 ..." />
            <Field label="Mot de passe" type="password" value={password} onChange={setPassword} testid="login-password" />
            <button disabled={busy} className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg hover:bg-[#A94520] transition active:scale-95 disabled:opacity-50" data-testid="login-submit">{busy ? "Connexion..." : "Se connecter"}</button>
          </form>
          <div className="flex items-center justify-between mt-2">
            <button type="button" onClick={() => { setMode("otp"); setSent(false); setCode(""); setDev(""); }} className="text-xs text-[#C85A32] font-semibold" data-testid="switch-otp">Se connecter avec un code</button>
            <Link to="/forgot-password" className="text-xs text-[#C85A32] font-semibold" data-testid="to-forgot">Mot de passe oublié ?</Link>
          </div>
        </>
      ) : (
        <>
          {!sent ? (
            <form onSubmit={requestCode} className="space-y-4">
              <Field label="Email ou téléphone" value={identifier} onChange={setIdentifier} testid="otp-login-identifier" placeholder="vous@exemple.com ou +228 ..." />
              <button disabled={busy} className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg hover:bg-[#A94520] transition active:scale-95 disabled:opacity-50" data-testid="otp-login-request">{busy ? "Envoi..." : "Recevoir un code"}</button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4">
              {dev && <p className="text-[11px] text-amber-600">Code (démo — envoi non délivré en préversion) : <strong className="font-num">{dev}</strong></p>}
              <Field label="Code reçu" value={code} onChange={setCode} testid="otp-login-code" placeholder="Code à 6 chiffres" />
              <button disabled={busy} className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg hover:bg-[#A94520] transition active:scale-95 disabled:opacity-50" data-testid="otp-login-verify">{busy ? "Connexion..." : "Se connecter"}</button>
              <button type="button" onClick={() => { setSent(false); setCode(""); }} className="w-full text-xs text-slate-500" data-testid="otp-login-back">Renvoyer / changer l'identifiant</button>
            </form>
          )}
          <button type="button" onClick={() => setMode("password")} className="mt-3 text-xs text-[#C85A32] font-semibold" data-testid="switch-password">Utiliser mon mot de passe</button>
        </>
      )}
      <SocialButtons context="login" />
      <p className="text-sm text-center text-slate-500 mt-5">
        Pas encore de compte ? <Link to="/register" className="text-[#C85A32] font-semibold" data-testid="to-register">Créer un compte</Link>
      </p>
    </AuthLayout>
  );
}

export function SocialButtons({ context = "login" }) {
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <>
      <div className="flex items-center gap-3 my-4"><div className="h-px bg-slate-200 flex-1" /><span className="text-xs text-slate-400">Ou continuez avec</span><div className="h-px bg-slate-200 flex-1" /></div>
      <div className="flex items-center gap-3">
        <button type="button" disabled title="Bientôt disponible"
          className="relative flex-1 border border-slate-200 rounded-lg py-3 flex items-center justify-center gap-2 text-slate-400 cursor-not-allowed" data-testid={`${context}-facebook`}>
          <Facebook className="w-5 h-5 text-[#1877F2]/60" /> <span className="font-semibold text-sm">Facebook</span>
          <span className="absolute -top-2 right-2 text-[9px] bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full">Bientôt</span>
        </button>
        <button type="button" onClick={google}
          className="flex-1 border border-slate-300 rounded-lg py-3 flex items-center justify-center gap-2 font-semibold text-sm hover:bg-slate-50 transition active:scale-95" data-testid={`${context}-google`}>
          <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Google
        </button>
      </div>
    </>
  );
}

export function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <img src={logoMark} alt="" className="h-11 w-auto" />
          <span className="font-display font-black text-2xl text-white tracking-tight">TOGOBATI</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl p-7">
          <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">{title}</h1>
          <p className="text-sm text-slate-500 mb-6">{subtitle}</p>
          {children}
        </div>
        <div className="mt-4"><InstallPWAButton variant="banner" /></div>
        <p className="text-center text-xs text-slate-400 mt-4">Matériaux & installation — Lomé · Agoè · Baguida</p>
      </div>
    </div>
  );
}

export function Field({ label, type = "text", value, onChange, testid, required = true, placeholder }) {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  const inputType = isPw ? (show ? "text" : "password") : type;
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <div className="relative">
        <input
          type={inputType} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
          className={`mt-1 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/50 focus:border-[#E08B00] ${isPw ? "pr-11" : ""}`}
          data-testid={testid}
        />
        {isPw && (
          <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid={`${testid}-toggle`}
            aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
            {show ? <EyeOff style={{ width: 18, height: 18 }} /> : <Eye style={{ width: 18, height: 18 }} />}
          </button>
        )}
      </div>
    </label>
  );
}
